import React, { useContext, useRef, useState } from 'react';

import styles from './AddPatientByCipDialog.module.css';

import Modal from '../../UI/Modal/Modal';
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import Dropdown from '../../UI/Dropdown/Dropdown';

import FiltersContext from '../../../context/FiltersContext';

import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';
import { useTranslation } from 'react-i18next';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { getEnvironmentApiUrl } from '../../../services/General';

interface Props {
    closeModal: () => void;
}

interface TownApiData {
    id?: string;
    name?: string;
    postal_code?: string;
    ine_code?: string;
    created_at?: string;
    updated_at?: string;
    deleted_at?: string;
}

interface PatientAddressApiData {
    id?: number;
    country?: string;
    state?: string;
    city?: string;
    zip?: string;
    street?: string;
    town_id?: string;
    full_address?: string;
    town?: TownApiData;
}

interface PatientApiData {
    id?: number;
    uuid?: string;
    email?: string;
    missed_qr?: boolean;
    special_attention?: boolean;
    first_name?: string;
    middle_name?: string;
    last_name_1?: string;
    last_name_2?: string;
    sex_id?: number;
    birth_date?: string;
    home_phone?: string;
    mobile_phone?: string;
    comment?: string;
    hospital_id?: number;
    primary_care_center_id?: number;
    addresses?: PatientAddressApiData[];
}

interface PatientResponseData {
    patient?: PatientApiData;
}

interface TownData {
    id?: string;
    name?: string;
    postalCode?: string;
    ineCode?: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

interface PatientAddressData {
    id?: number;
    country?: string;
    state?: string;
    city?: string;
    zip?: string;
    street?: string;
    townId?: string;
    fullAddress?: string;
    town?: TownData;
}

interface PatientDemographics {
    id?: number;
    uuid?: string;
    email?: string;
    missedQr?: boolean;
    specialAttention?: boolean;
    firstName: string;
    middleName: string;
    lastName1: string;
    lastName2: string;
    sexId?: number;
    birthDate: string;
    homePhone?: string;
    mobilePhone?: string;
    comment?: string;
    hospitalId?: number;
    primaryCareCenterId?: number;
    firstAddress?: PatientAddressData;
    cip: string;
}

const AddPatientByCipDialog: React.FC<Props> = ({ closeModal }) => {
    const { t } = useTranslation();
    const { error } = useToast();
    const { profiles } = useContext(FiltersContext);

    const { sendRequest: getPatientByCipRequest } = useHttp<PatientResponseData>();
    const { sendRequest: registerCaseRequest } = useHttp();

    const [cip, setCip] = useState('');
    const [step, setStep] = useState<'cip' | 'confirm'>('cip');
    const [patientData, setPatientData] = useState<PatientDemographics | null>(null);
    const [selectedEnvironmentId, setSelectedEnvironmentId] = useState('');
    const [showEnvironmentDropdown, setShowEnvironmentDropdown] = useState(false);

    const environmentRef = useRef<HTMLButtonElement | null>(null);

    const HARDCODED_TOKEN = '';

    const selectedProfile = profiles.find(profile => String(profile.id) === selectedEnvironmentId);

    const handleChangeCip = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setCip(evt.target.value.replace(/\s+/g, ''));
    };

    const handleSelectEnvironment = (profileId: number) => {
        setSelectedEnvironmentId(String(profileId));
        setShowEnvironmentDropdown(false);
    };

    const getFullName = (patient: PatientDemographics) => {
        return [
            patient.firstName,
            patient.middleName,
            patient.lastName1,
            patient.lastName2
        ].filter(Boolean).join(' ');
    };

    const mapFirstAddress = (address?: PatientAddressApiData): PatientAddressData | undefined => {
        if (!address) return undefined;

        return {
            id: address.id,
            country: address.country,
            state: address.state,
            city: address.city,
            zip: address.zip,
            street: address.street,
            townId: address.town_id,
            fullAddress: address.full_address,
            town: address.town ? {
                id: address.town.id,
                name: address.town.name,
                postalCode: address.town.postal_code,
                ineCode: address.town.ine_code,
                createdAt: address.town.created_at,
                updatedAt: address.town.updated_at,
                deletedAt: address.town.deleted_at,
            } : undefined
        };
    };

    const parsePatientDemographics = (responseData: PatientResponseData | undefined): PatientDemographics | null => {
        if (!responseData) return null;

        const patient = responseData.patient;

        if (!patient) return null;

        const parsedPatient: PatientDemographics = {
            id: patient.id,
            uuid: patient.uuid,
            email: patient.email,
            missedQr: patient.missed_qr,
            specialAttention: patient.special_attention,
            firstName: patient.first_name ?? '',
            middleName: patient.middle_name ?? '',
            lastName1: patient.last_name_1 ?? '',
            lastName2: patient.last_name_2 ?? '',
            sexId: patient.sex_id,
            birthDate: patient.birth_date ?? '',
            homePhone: patient.home_phone,
            mobilePhone: patient.mobile_phone,
            comment: patient.comment,
            hospitalId: patient.hospital_id,
            primaryCareCenterId: patient.primary_care_center_id,
            firstAddress: mapFirstAddress(patient.addresses?.[0]),
            cip: cip.trim()
        };

        if (!parsedPatient.firstName || !parsedPatient.lastName1) return null;

        return parsedPatient;
    };

    const handlePatientLoaded = (responseData: PatientResponseData) => {
        const parsedPatient = parsePatientDemographics(responseData);

        if (!parsedPatient) {
            error(t('patient_not_found_by_cip') as string);
            return;
        }

        setPatientData(parsedPatient);
        setSelectedEnvironmentId('');
        setShowEnvironmentDropdown(false);
        setStep('confirm');
    };

    const handlePatientNotFound = () => {
        setPatientData(null);
        setSelectedEnvironmentId('');
        setShowEnvironmentDropdown(false);
        setStep('cip');
        error(t('patient_not_found_by_cip') as string);
    };

    const handleSearchPatient = () => {
        if (!cip.trim()) {
            error(t('cip_required') as string);
            return;
        }

        getPatientByCipRequest(
            {
                url: `${getEnvironmentApiUrl()}patient/patient_data_cip/${cip.trim()}`,
                method: 'GET',
                headers: HARDCODED_TOKEN
                    ? {
                        accept: 'application/json',
                        'x-access-token': HARDCODED_TOKEN
                    }
                    : {
                        accept: 'application/json'
                    }
            },
            handlePatientLoaded,
            handlePatientNotFound
        );
    };

    const handleCaseRegistered = () => {
        closeModal();
    };

    const buildRegisterCasePayload = (patient: PatientDemographics) => {
        return {
            patient: {
                first_name: patient.firstName,
                last_name_1: patient.lastName1,
                last_name_2: patient.lastName2,
                ...(patient.firstAddress
                    ? {
                        addresses: [
                            {
                                country: patient.firstAddress.country,
                                state: patient.firstAddress.state,
                                city: patient.firstAddress.city,
                                zip: patient.firstAddress.zip,
                                street: patient.firstAddress.street,
                                town_id: patient.firstAddress.townId,
                                full_address: patient.firstAddress.fullAddress
                            }
                        ]
                    }
                    : {})
            },
            case: {},
            profile_follow_up: {
                bed_id: null,
                profile_id: Number(selectedEnvironmentId),
                is_home_pfu: true
            }
        };
    };

    const handleConfirmAddPatient = () => {
        if (!patientData) return;

        if (!selectedEnvironmentId) {
            error(t('environment_required') as string);
            return;
        }

        registerCaseRequest(
            {
                url: `${getEnvironmentApiUrl()}case/register_case`,
                method: 'POST',
                headers: HARDCODED_TOKEN
                    ? {
                        accept: 'application/json',
                        'x-access-token': HARDCODED_TOKEN
                    }
                    : {
                        accept: 'application/json'
                    },
                body: buildRegisterCasePayload(patientData)
            },
            handleCaseRegistered,
            () => {
                error(t('invalid_environment_for_register_case') as string);
            }
        );
    };

    const handleBackToSearch = () => {
        setStep('cip');
        setPatientData(null);
        setSelectedEnvironmentId('');
        setShowEnvironmentDropdown(false);
    };

    return (
        <Modal
            show={true}
            setShowModal={closeModal}
            classesModal={styles.addPatientModal}
        >
            <div className={styles.container}>
                <h2 className={styles.title}>{t('add_patient')}</h2>

                {step === 'cip' && (
                    <>
                        <p className={styles.text}>{t('enter_patient_cip')}</p>

                        <div className={styles.inputWrapper}>
                            <input
                                type="text"
                                value={cip}
                                onChange={handleChangeCip}
                                placeholder={String(t('cip'))}
                                className={styles.cipInput}
                            />
                        </div>

                        <div className={styles.actions}>
                            <CancelConfirmButtons
                                onCancel={closeModal}
                                onConfirm={handleSearchPatient}
                                cancelName={t('cancel') as string}
                                confirmName={t('continue') as string}
                            />
                        </div>
                    </>
                )}

                {step === 'confirm' && patientData && (
                    <>
                        <p className={styles.text}>{t('confirm_patient_data')}</p>

                        <div className={styles.dataContainer}>
                            <p>
                                <strong>{t('patient')}:</strong> {getFullName(patientData)}
                            </p>
                            <p>
                                <strong>{t('birth_date')}:</strong> {patientData.birthDate}
                            </p>
                            <p>
                                <strong>{t('cip')}:</strong> {patientData.cip}
                            </p>
                        </div>

                        <div className={styles.selectBlock}>
                            <button
                                type="button"
                                ref={environmentRef}
                                className={styles.environmentButton}
                                onClick={() => setShowEnvironmentDropdown(prev => !prev)}
                            >
                                <span style={{ color: selectedProfile ? '#4f4f4f' : '#9e9e9e' }}>
                                    {selectedProfile?.name ?? t('select_environment')}
                                </span>
                                <MdKeyboardArrowDown />
                            </button>

                            <Dropdown
                                show={showEnvironmentDropdown}
                                elementRef={environmentRef}
                                handleClickOutside={() => setShowEnvironmentDropdown(false)}
                            >
                                <div className={styles.environmentDropdown}>
                                    {profiles.map((profile) => (
                                        <button
                                            key={profile.id}
                                            type="button"
                                            className={styles.environmentOption}
                                            onClick={() => handleSelectEnvironment(profile.id)}
                                        >
                                            {profile.name}
                                        </button>
                                    ))}
                                </div>
                            </Dropdown>
                        </div>

                        <div className={styles.actions}>
                            <CancelConfirmButtons
                                onCancel={handleBackToSearch}
                                onConfirm={handleConfirmAddPatient}
                                cancelName={t('cancel') as string}
                                confirmName={t('confirm') as string}
                            />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default AddPatientByCipDialog;